import { createContext, useCallback, useContext, useEffect, useMemo, useState, type ReactNode } from "react";
import { useLocation } from "react-router-dom";
import { useUser } from "./UserContext";
import { isTutorialActive } from "../tutorials/engine/session";

export interface VisitEntry {
  /** Pathname without the query — identity of the page, so revisiting with new filters is not a new entry. */
  key: string;
  /** Full path (query included) to return to. */
  path: string;
  /** The page's own title, as registered by PageHeader. */
  label: string;
}

/** Five are shown; the sixth covers the current page, which is filtered out of the list. */
const KEEP = 6;

const STORAGE_KEY = "glut_recent_pages";

/**
 * The trail is stamped with whose it is. A shared machine keeps one key, so the next user to
 * sign in finds a mismatched stamp and starts empty rather than reading the last user's
 * document numbers out of the list.
 */
interface StoredTrail {
  userId: number;
  entries: VisitEntry[];
}

/** Storage is user-editable, and these strings end up in a link — trust nothing in it. */
function isEntry(value: unknown): value is VisitEntry {
  if (!value || typeof value !== "object") return false;
  const { key, path, label } = value as Record<string, unknown>;
  if (typeof key !== "string" || typeof path !== "string" || typeof label !== "string") return false;
  // In-app routes only: no protocol-relative or absolute URLs pointing off the app.
  return path.startsWith("/") && !path.startsWith("//") && key.startsWith("/") && label !== "";
}

function loadTrail(userId: number): VisitEntry[] {
  try {
    const raw = localStorage.getItem(STORAGE_KEY);
    if (!raw) return [];
    const parsed = JSON.parse(raw) as StoredTrail | null;
    if (parsed?.userId !== userId || !Array.isArray(parsed.entries)) return [];
    return parsed.entries.filter(isEntry).slice(0, KEEP);
  } catch {
    return []; // corrupt or unreadable storage just means no history
  }
}

function saveTrail(userId: number, entries: VisitEntry[]) {
  // A tutorial's pages belong to its sample user, not to whoever is signed in: keep them
  // in memory and leave the stored trail for the real session to come back to.
  if (isTutorialActive()) return;
  try {
    localStorage.setItem(STORAGE_KEY, JSON.stringify({ userId, entries } satisfies StoredTrail));
  } catch {
    // private mode or a full quota; the in-memory trail still works
  }
}

interface HistoryContextValue {
  /** Most recent first, current page at the front. */
  entries: VisitEntry[];
  record: (path: string, label: string) => void;
}

const HistoryContext = createContext<HistoryContextValue>({ entries: [], record: () => {} });

export function HistoryProvider({ children }: { children: ReactNode }) {
  // ProtectedRoute holds this subtree back until /me has answered, so the user is known on
  // the first render and the trail can be read straight into the initial state — before any
  // page below has had a chance to record itself.
  const { user } = useUser();
  const userId = user?.id;
  const [entries, setEntries] = useState<VisitEntry[]>(() => (userId == null ? [] : loadTrail(userId)));

  useEffect(() => {
    if (userId == null) return;
    saveTrail(userId, entries);
  }, [userId, entries]);

  const record = useCallback((path: string, label: string) => {
    const key = path.split("?")[0];
    setEntries((prev) => {
      // Re-rendering the same page must not produce a new array, or the effect that
      // records it would loop.
      const head = prev[0];
      if (head?.key === key && head.path === path && head.label === label) return prev;
      return [{ key, path, label }, ...prev.filter((entry) => entry.key !== key)].slice(0, KEEP);
    });
  }, []);

  const value = useMemo(() => ({ entries, record }), [entries, record]);
  return <HistoryContext.Provider value={value}>{children}</HistoryContext.Provider>;
}

export function useVisitHistory() {
  return useContext(HistoryContext);
}

/**
 * Records the current route under a readable name — called by PageHeader, so the trail is
 * named after what the page calls itself ("Material Requests", "MRQ-2026-0001") rather than
 * its URL. Detail pages render their header only once loaded, so the entry lands with the
 * document number already in it.
 */
export function useRecordVisit(label: string | null | undefined) {
  const { record } = useVisitHistory();
  const location = useLocation();
  const path = location.pathname + location.search;

  useEffect(() => {
    if (!label) return;
    record(path, label);
  }, [path, label, record]);
}
